import { useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import useChangePassword from "../lib/fetchMerchantPassChange";

const defaultForm = {
  oldPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const PasswordInput = ({ label, name, value, onChange, show, onToggle, autoComplete }) => (
  <label className="dashboard-form-field">
    <span>{label}</span>
    <div className="dashboard-password-field">
      <input
        type={show ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        required
        minLength={6}
        placeholder="••••••••"
      />
      <button
        type="button"
        className="dashboard-password-field__toggle"
        onClick={onToggle}
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <FiEyeOff aria-hidden="true" /> : <FiEye aria-hidden="true" />}
      </button>
    </div>
  </label>
);

const ChangePasswordForm = ({ embedded = true }) => {
  const [form, setForm] = useState(defaultForm);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [localError, setLocalError] = useState("");

  const { loading, error, success, changePassword } = useChangePassword();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setLocalError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");

    if (form.newPassword.length < 6) {
      setLocalError("New password must be at least 6 characters.");
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setLocalError("New password and confirmation do not match.");
      return;
    }
    if (form.oldPassword === form.newPassword) {
      setLocalError("New password must be different from your current password.");
      return;
    }

    await changePassword(form.oldPassword, form.newPassword);
    setForm(defaultForm);
    setShowOld(false);
    setShowNew(false);
    setShowConfirm(false);
  };

  const displayError = localError || error;

  return (
    <form onSubmit={handleSubmit} className={`dashboard-security-form${embedded ? "" : " merchant-password-card"}`}>
      {!embedded && <h3 className="dashboard-security-form__title">Change password</h3>}

      <p className="dashboard-form-hint">
        Use at least 6 characters. You will stay signed in after updating your password.
      </p>

      <div className="dashboard-security-form__grid">
        <PasswordInput
          label="Current password *"
          name="oldPassword"
          value={form.oldPassword}
          onChange={handleChange}
          show={showOld}
          onToggle={() => setShowOld((v) => !v)}
          autoComplete="current-password"
        />
        <PasswordInput
          label="New password *"
          name="newPassword"
          value={form.newPassword}
          onChange={handleChange}
          show={showNew}
          onToggle={() => setShowNew((v) => !v)}
          autoComplete="new-password"
        />
        <PasswordInput
          label="Confirm new password *"
          name="confirmPassword"
          value={form.confirmPassword}
          onChange={handleChange}
          show={showConfirm}
          onToggle={() => setShowConfirm((v) => !v)}
          autoComplete="new-password"
        />
      </div>

      {displayError && (
        <div className="dashboard-banner dashboard-banner--error" role="alert">
          {displayError}
        </div>
      )}

      {success && (
        <div className="dashboard-banner dashboard-banner--success" role="status">
          {success}
        </div>
      )}

      <div className="dashboard-form-actions dashboard-security-form__actions">
        <button type="submit" className="dashboard-btn dashboard-btn--primary" disabled={loading}>
          {loading ? "Updating…" : "Update password"}
        </button>
      </div>
    </form>
  );
};

export default ChangePasswordForm;
